// Plan 17-07 Task 2 — Login buttons re-skin (POLISH-13).
// Folk-art Phase 3 — folk sign-in disclosure (prototype SignBtn): Apple reads
// as the dark continue button, Google as the light/near-white card button —
// flat folk surfaces rather than the chunky 3D CTA so they sit quietly under
// the hero. The Btn primitive is kept (its busy/disabled/sfx/a11y wiring is
// preserved); only the surface is restyled via the `style` prop. Click
// handlers + i18n keys are untouched (useAuth().loginWithApple/Google).

import type { CSSProperties, JSX } from 'react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useAuth } from '../../auth/AuthProvider.js'
import { Btn } from '../components/Btn.js'

// Apple → dark continue button (ink surface, white label).
const APPLE_STYLE: CSSProperties = {
  background: 'var(--color-ink)',
  color: 'var(--color-on-action)',
  boxShadow: 'var(--shadow-card)',
}

// Google → light card button (near-white surface, ink label, warm hairline).
const GOOGLE_STYLE: CSSProperties = {
  background: 'var(--color-card)',
  color: 'var(--color-text-primary)',
  boxShadow:
    'inset 0 0 0 2px color-mix(in srgb, var(--color-ink-soft) 28%, transparent), var(--shadow-card)',
}

export function LoginButtons(): JSX.Element {
  const { loginWithApple, loginWithGoogle } = useAuth()
  const intl = useIntl()
  const [busy, setBusy] = useState<'apple' | 'google' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const signingInLabel = intl.formatMessage({
    id: 'auth.login.signing_in',
    defaultMessage: 'Signing in…',
  })

  async function clickApple(): Promise<void> {
    setError(null)
    setBusy('apple')
    try {
      await loginWithApple()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  async function clickGoogle(): Promise<void> {
    setError(null)
    setBusy('google')
    try {
      await loginWithGoogle()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      className="login-buttons"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        width: '100%',
      }}
    >
      <Btn
        kind="them"
        size="lg"
        block
        style={APPLE_STYLE}
        onClick={clickApple}
        disabled={busy !== null}
      >
        {busy === 'apple' ? (
          signingInLabel
        ) : (
          <FormattedMessage
            id="auth.login.apple"
            defaultMessage="Continue with Apple"
          />
        )}
      </Btn>
      <Btn
        kind="them"
        size="lg"
        block
        style={GOOGLE_STYLE}
        onClick={clickGoogle}
        disabled={busy !== null}
      >
        {busy === 'google' ? (
          signingInLabel
        ) : (
          <FormattedMessage
            id="auth.login.google"
            defaultMessage="Continue with Google"
          />
        )}
      </Btn>
      {error && (
        <p
          className="login-error"
          role="alert"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-13)',
            color: 'var(--color-cta-destructive)',
          }}
        >
          <FormattedMessage
            id="auth.login.error"
            defaultMessage="Sign-in failed."
          />{' '}
          {error}
        </p>
      )}
    </div>
  )
}
